import { useRef, useState } from 'react'
import { useFormState } from 'react-final-form'
import cx from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { SplitSizeData } from 'workflow/interface'
import { fetchCampaignData } from 'workflow/Workflow.actions'

import Button from 'common/components/button'
import { TextareaType } from 'common/components/dynamicFields/interfaces'
import Toggle from 'common/components/toggle/Toggle'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { patchControlLine } from 'features/unifiedFlow/api/api'
import { isComponentElement } from 'features/unifiedFlow/api/interfaces'
import useGenerateCustomVariantsMutation from 'features/unifiedFlow/api/mutations/useGenerateCustomVariantsMutation'
import ControlSubjectLine from 'features/unifiedFlow/components/templateSelection/setupExperiment/ControlSubjectLine'
import ExperimentName from 'features/unifiedFlow/components/templateSelection/setupExperiment/ExperimentName'
import Project from 'features/unifiedFlow/components/templateSelection/setupExperiment/Project'
import SendDate from 'features/unifiedFlow/components/templateSelection/setupExperiment/SendDate'
import SplitCalculator from 'features/unifiedFlow/components/templateSelection/splitCalculator'
import useWebsocketMessage from 'features/unifiedFlow/contentPage/hooks/websocketMessage'
import { getWsTopic } from 'features/unifiedFlow/contentPage/hooks/websocketMessage/helpers'

import { getStepStatus } from '../../../helpers'
import { useContent, useSelectedElement } from '../../../hooks'

import ExperimentSettingsFooter from './ExperimentSettingsFooter'

import classes from './ExperimentSettings.module.css'

const ExperimentSettingsFormContent = ({
  optimizationContentType,
  optimizationSettings,
  projectSplitCalculatorConfiguration,
}) => {
  const resetCalculatorCallback = useRef<() => void>(() => {})
  const [isCalculatorLoading, setIsCalculatorLoading] = useState(false)
  const dispatch = useAppDispatch()
  const { content, refetch: refetchContent } = useContent()
  const { data: selectedElement } = useSelectedElement()
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const subjectLines = useAppSelector(
    (state) => state.campaignStates.subjectLines
  )
  const selectedTopics = useAppSelector(
    (state) => state.unifiedFlow.selectedTopics
  )
  const formState = useFormState()
  const generateCustomVariantsMutation = useGenerateCustomVariantsMutation()
  const { sendWebsocketMessage } = useWebsocketMessage()

  const hasAiControlLines = subjectLines.some(
    (v) => v.reason_included === 'Control Line'
  )
  const initialAiControlLine = hasAiControlLines
  const [aiControlLine, setAiControlLine] =
    useState<boolean>(initialAiControlLine)
  const isDirty =
    aiControlLine !== initialAiControlLine ||
    (!aiControlLine && !!formState.dirtyFields.controlSubjectLine)

  const initialSplitCalculationResult: SplitSizeData = (
    optimizationSettings as any
  )?.split_calculation.configuration //TODO: Remove casting when SplitCalculationConfigDto matches SplitSizeData

  const splitCalculationResultRef = useRef<SplitSizeData | undefined>(
    initialSplitCalculationResult
  )

  const dateConfig =
    projectSplitCalculatorConfiguration?.experimentMainFields?.find(
      (field) => field.name === 'iso_send_date'
    )
  const splitCalculatorConfiguration =
    projectSplitCalculatorConfiguration?.splitCalculation
  const flags = useFlags()
  const controlSubjectLineConfig =
    projectSplitCalculatorConfiguration?.experimentMainFields?.find(
      (field) => field.name === 'own_subject_line'
    )
  const integrationType = projectSplitCalculatorConfiguration?.integrationType

  const briefStepsStatus = getStepStatus(content?.steps_status, 'brief')
  const isBriefStepsStatusReadOnly = briefStepsStatus === 'read-only'

  const handleSaveControlLine = async () => {
    if (!selectedElement || !content) {
      return
    }
    try {
      await patchControlLine({
        accountId,
        contentId: content._id,
        elementId: selectedElement.element_id,
        ownSubjectLine: aiControlLine
          ? null
          : (formState.values.controlSubjectLine ?? ''),
        ownSubjectLineAiGenerated: aiControlLine,
      })
    } catch {
      // proceed regardless
    }

    if (aiControlLine) {
      const wsTopic = getWsTopic({
        data: {
          contentId: content._id,
          elementId: selectedElement.element_id,
        },
        action: 'generateElementVariants',
      })
      sendWebsocketMessage({
        action: 'generateElementVariants',
        data: {
          contentId: content._id,
          elementId: selectedElement.element_id,
        },
        subscriptionAction: 'subscribe',
      })
      generateCustomVariantsMutation.mutate(
        {
          accountId,
          topics: selectedTopics,
          optimizationConfiguration: optimizationSettings,
          contentId: content._id,
          elementId: selectedElement.element_id,
          wsTopic,
        },
        {
          onSuccess: () => {
            refetchContent()
            if (
              isComponentElement(selectedElement) &&
              !!selectedElement?.campaign_id
            ) {
              dispatch(fetchCampaignData(selectedElement?.campaign_id))
            }
          },
        }
      )
    }
  }

  return (
    <>
      <div className={cx(classes.experimentSettingsFormContent)}>
        <ExperimentName
          isDisabled={isBriefStepsStatusReadOnly}
          name="experimentName"
        />
        <Project
          name="project"
          mode="edit"
          data-testid="project-select"
          isDisabled
          optimizationContentType={optimizationContentType}
        />
        {!flags.jacquardGeneratedControl && controlSubjectLineConfig && (
          <ControlSubjectLine
            name="controlSubjectLine"
            config={controlSubjectLineConfig as TextareaType}
            integrationType={integrationType}
            isDisabled={isBriefStepsStatusReadOnly}
          />
        )}
        {flags.jacquardGeneratedControl && (
          <>
            <hr className="my-2 border-gold-400" />
            <div className="flex flex-col gap-3">
              <Toggle
                label="Let Jacquard automate a control line."
                checked={aiControlLine}
                onChange={setAiControlLine}
                disabled={isBriefStepsStatusReadOnly}
                className="py-2"
              />
              {!aiControlLine && controlSubjectLineConfig && (
                <ControlSubjectLine
                  name="controlSubjectLine"
                  config={controlSubjectLineConfig as TextareaType}
                  integrationType={integrationType}
                  isDisabled={isBriefStepsStatusReadOnly}
                />
              )}
              {isDirty && (
                <Button
                  type="button"
                  variant="primary"
                  size="small"
                  className="self-end px-6"
                  onClick={handleSaveControlLine}
                >
                  Update
                </Button>
              )}
            </div>
            <hr className="my-2 border-gold-400" />
          </>
        )}
        {dateConfig && (
          <SendDate
            isDisabled={isBriefStepsStatusReadOnly}
            name="sendDate"
            config={dateConfig}
            data-testid="send-date"
          />
        )}

        {splitCalculatorConfiguration && (
          <SplitCalculator
            configuration={splitCalculatorConfiguration}
            splitCalculationResult={initialSplitCalculationResult}
            onLoading={setIsCalculatorLoading}
            onResult={(result) => (splitCalculationResultRef.current = result)}
            resetCalculatorCallback={(resetCallback) =>
              (resetCalculatorCallback.current = resetCallback)
            }
            isDisabled={isBriefStepsStatusReadOnly}
          />
        )}
      </div>
      <ExperimentSettingsFooter
        isDisabled={isCalculatorLoading || isBriefStepsStatusReadOnly}
        splitCalculationResult={splitCalculationResultRef.current}
        optimizationSettingsConfiguration={optimizationSettings}
        onReset={() => resetCalculatorCallback.current?.()}
      />
    </>
  )
}

export default ExperimentSettingsFormContent
